import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatZAR, formatApiError } from "@/lib/api";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PROVINCES = ["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Free State", "Limpopo", "Mpumalanga", "North West", "Northern Cape"];
const PAYMENT_METHODS = [
  { id: "yoco", label: "Yoco Card", subtitle: "Visa · Mastercard via Yoco" },
  { id: "payfast", label: "PayFast", subtitle: "EFT · SnapScan · Zapper" },
  { id: "ozow", label: "Ozow Instant EFT", subtitle: "Direct from your bank" },
  { id: "payflex", label: "Payflex", subtitle: "Pay in 4 interest-free installments" },
  { id: "apple_pay", label: "Apple Pay" },
  { id: "google_pay", label: "Google Pay" },
];

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [paymentMethod, setPaymentMethod] = useState("yoco");
  const [customer, setCustomer] = useState({
    email: user && user.email ? user.email : "",
    first_name: "",
    last_name: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    province: "Gauteng",
    postal_code: "",
    country: "South Africa",
  });

  const shipping = shippingMethod === "express" ? 350 : (subtotal >= 2000 ? 0 : 150);
  const total = subtotal + shipping;

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-3xl">Your bag is empty</h1>
        <Link to="/shop" className="btn-primary mt-6">Shop wigs</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        customer,
        shipping_method: shippingMethod,
        payment_method: paymentMethod,
        notes: "",
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          length: i.length,
          density: i.density,
          color: i.color,
          lace_type: i.lace_type,
          unit_price: i.price,
        })),
      };
      const { data } = await api.post("/checkout", payload);
      clear();
      toast.success("Order placed. Thank you!");
      navigate(`/order/${data.order_number}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setCustomer({ ...customer, [k]: e.target.value });

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-6 py-16">
      <p className="small-label">Checkout</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-3">Almost yours.</h1>

      <form onSubmit={submit} className="mt-10 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          {/* Contact */}
          <section>
            <p className="small-label mb-3">Contact</p>
            <input required type="email" placeholder="Email" value={customer.email} onChange={upd("email")} data-testid="checkout-email" className="w-full bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-charcoal" />
            {!user && (
              <p className="text-xs text-stone mt-2">Have an account? <Link to="/login" className="underline">Login</Link></p>
            )}
          </section>

          {/* Shipping address */}
          <section>
            <p className="small-label mb-3">Shipping address</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <input required placeholder="First name" value={customer.first_name} onChange={upd("first_name")} data-testid="checkout-first-name" className="bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm" />
              <input required placeholder="Last name" value={customer.last_name} onChange={upd("last_name")} data-testid="checkout-last-name" className="bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm" />
              <input required placeholder="Phone" value={customer.phone} onChange={upd("phone")} data-testid="checkout-phone" className="sm:col-span-2 bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm" />
              <input required placeholder="Street address" value={customer.address_line1} onChange={upd("address_line1")} data-testid="checkout-address1" className="sm:col-span-2 bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm" />
              <input placeholder="Apartment, suite, etc. (optional)" value={customer.address_line2} onChange={upd("address_line2")} data-testid="checkout-address2" className="sm:col-span-2 bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm" />
              <input required placeholder="City" value={customer.city} onChange={upd("city")} data-testid="checkout-city" className="bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm" />
              <select required value={customer.province} onChange={upd("province")} data-testid="checkout-province" className="bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm">
                {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <input required placeholder="Postal code" value={customer.postal_code} onChange={upd("postal_code")} data-testid="checkout-postal" className="bg-ivory border border-stone-line rounded-2xl px-4 py-3 text-sm" />
              <input value={customer.country} disabled className="bg-nude border border-stone-line rounded-2xl px-4 py-3 text-sm" />
            </div>
          </section>

          {/* Shipping method */}
          <section>
            <p className="small-label mb-3">Shipping method</p>
            <div className="space-y-3">
              <label className={`flex items-center justify-between p-4 rounded-2xl border cursor-pointer ${shippingMethod === "standard" ? "border-charcoal" : "border-stone-line"}`}>
                <span><input data-testid="ship-standard" type="radio" name="ship" className="mr-3" checked={shippingMethod === "standard"} onChange={() => setShippingMethod("standard")} /> Standard 3–5 business days</span>
                <span className="text-sm">{subtotal >= 2000 ? "Free" : "R 150"}</span>
              </label>
              <label className={`flex items-center justify-between p-4 rounded-2xl border cursor-pointer ${shippingMethod === "express" ? "border-charcoal" : "border-stone-line"}`}>
                <span><input data-testid="ship-express" type="radio" name="ship" className="mr-3" checked={shippingMethod === "express"} onChange={() => setShippingMethod("express")} /> Express 1–2 business days</span>
                <span className="text-sm">R 350</span>
              </label>
            </div>
          </section>

          {/* Payment */}
          <section>
            <p className="small-label mb-3">Payment</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {PAYMENT_METHODS.map((m) => (
                <label key={m.id} className={`p-4 rounded-2xl border cursor-pointer ${paymentMethod === m.id ? "border-charcoal" : "border-stone-line"}`}>
                  <input data-testid={`pay-${m.id}`} type="radio" name="pay" className="mr-3" checked={paymentMethod === m.id} onChange={() => setPaymentMethod(m.id)} />
                  <span className="font-medium text-sm">{m.label}</span>
                  {m.subtitle && <p className="text-xs text-stone mt-1 ml-7">{m.subtitle}</p>}
                </label>
              ))}
            </div>
            <p className="text-xs text-stone mt-3 inline-flex items-center gap-1.5">
              <ShieldCheck strokeWidth={1.5} className="w-4 h-4" /> Secure encrypted checkout. Yoco activates once merchant credentials are set in admin.
            </p>
          </section>
        </div>

        {/* Order Summary */}
        <aside className="bg-nude rounded-3xl p-8 h-fit sticky top-24">
          <p className="small-label">Order summary</p>
          <div className="mt-5 space-y-4">
            {items.map((i) => (
              <div key={i.key} className="flex gap-3 items-center">
                <div className="relative">
                  <img src={i.image} alt={i.title} className="w-14 h-16 object-cover rounded-xl bg-ivory" />
                  <span className="absolute -top-2 -right-2 bg-charcoal text-ivory text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{i.quantity}</span>
                </div>
                <div className="flex-1 text-sm">
                  <p className="leading-tight">{i.title}</p>
                  <p className="text-xs text-stone">{i.length}″ · {i.density}</p>
                </div>
                <p className="text-sm">{formatZAR(i.price * i.quantity)}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 space-y-2 text-sm border-t border-stone-line pt-5">
            <div className="flex justify-between"><span>Subtotal</span><span>{formatZAR(subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : formatZAR(shipping)}</span></div>
            <div className="flex justify-between font-medium text-base border-t border-stone-line pt-3">
              <span>Total</span><span data-testid="checkout-total">{formatZAR(total)}</span>
            </div>
          </div>
          <button type="submit" disabled={submitting} data-testid="place-order-button" className="btn-primary w-full mt-6">
            {submitting ? "Processing…" : "Place order"}
          </button>
        </aside>
      </form>
    </div>
  );
}
